import operationTypes from 'constants/operationTypes';
import { PaginationResponse } from 'modules/_shared/components/Pagination/Pagination';
import { getDocumentsByIds } from 'modules/documents/api/getDocumentsByIds';
import { getSharesTitlesByPartnerOperations } from 'modules/partners/api/getSharesTitlesByPartnerOperations';
import { getOperationsByPartner } from 'modules/transactions/api/getOperationsByPartner';
import { operationsPartnerTransactions } from 'modules/transactions/constants/operationsPartnerTransactions';
import { useCallback, useEffect, useState } from 'react';
import { store } from 'redux/store';
import { Operation, Partner, SharesTitle, Society, Transaction } from 'types';
import { getPartnerContributionData } from 'utils/getPartnerAmountFromContributions';

type UsePartnerTransactionsProps = {
  partner: Partner;
  society: Society;
  page: number;
  items: number;
  sortBy?: string;
  sortOrder?: string;
};

type PartnerOperationsResponse = {
  data: Operation[];
  pagination: PaginationResponse;
};

export const excludedOperations = [
  operationTypes.ADD_PARTNER,
  operationTypes.ADD_PARTNERS,
  operationTypes.ADD_BENEFICIARY,
  operationTypes.STOCK_OPTION,
  operationTypes.PHANTOM_SHARE,
];

const formatAlias = (operation: Operation) => {
  const operationsWithoutAlias = [
    operationTypes.HERITAGE,
    operationTypes.DONATION,
    operationTypes.ENDORSEMENT,
    operationTypes.TRANSMISSION,
  ];

  if (operationsWithoutAlias.includes(operation.operationType)) {
    return operation.operationType;
  }

  return operation?.alias || '';
};

const isOperationValidWithNegative = (operation: Operation): boolean => {
  const operationsWithNegative = [
    operationTypes.SELL_PARTICIPATION,
    operationTypes.HERITAGE,
    operationTypes.DONATION,
    operationTypes.ENDORSEMENT,
    operationTypes.TRANSMISSION,
  ];

  if (operationsWithNegative.includes(operation.operationType)) {
    return true;
  }
  if (operation?.isNegative) {
    return false;
  }

  return true;
};

const mapOperationsToTransactions = (
  operations: Operation[],
  partner: Partner
): any => {
  const transactions: Record<string, any>[] = [];

  const operationsStoredInPartner = [
    operationTypes.RENUMERATION,
    operationTypes.RECLASSIFICATION,
    operationTypes.SPLIT,
  ];

  operations
    // TODO: [AXA] - Avois filter isNegative in the frontend
    ?.filter((operation) => isOperationValidWithNegative(operation))
    ?.forEach((operation) => {
      const alias = formatAlias(operation);

      if (operationsStoredInPartner.includes(operation.operationType)) {
        transactions.push({
          ...operation,
          transactionType: operation.operationType,
          operation: operation._id,
          partner: partner._id,
          alias,
        });
        return;
      }

      if (operation?.operationType === operationTypes.CONTRIBUTION) {
        const contributionData = getPartnerContributionData(
          partner?._id,
          operation
        );
        transactions.push({
          ...contributionData,
          transactionType: operationTypes.CONTRIBUTION,
          date: operation.date,
          society: operation.society,
          operation: operation['_id'],
          documents: operation?.documents,
          comments: operation?.comments || '',
          alias,
        });
        return;
      }

      (operation.transactions as any[])?.forEach((transaction) =>
        transactions.push({
          ...transaction,
          nominalValue: operation.nominalValue || 0,
          currency: operation.currency,
          date: operation.date,
          operation: operation['_id'],
          society: operation.society,
          documents: operation?.documents,
          comments: operation?.comments || '',
          alias,
        })
      );
    });

  return transactions;
};

const getDocumentIdsFromTransactions = (
  transactions: Transaction[]
): string[] => {
  if (!transactions?.length) return [];

  const documentIds = transactions
    .map((transaction) => transaction?.documents)
    .flat()
    .filter(Boolean) as string[];
  return Array.from(new Set(documentIds));
};

export const usePartnerTransactions = ({
  partner,
  society,
  page,
  items,
  sortBy,
  sortOrder,
}: UsePartnerTransactionsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSharesTitles, setIsLoadingSharesTitles] = useState(false);

  const [partnerTransactions, setPartnerTransactions] = useState([]);
  const [partnerShareTitles, setPartnerShareTitles] = useState<SharesTitle[]>(
    []
  );
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [documents, setDocuments] = useState<Document[]>([]);

  const fetchPartnerTransactions = useCallback(async () => {
    if (!partner) return;

    try {
      setIsLoading(true);

      const params = { page, items, sortBy, sortOrder };
      const filteredOperations = (await getOperationsByPartner({
        societyId: society._id,
        partnerId: partner._id,
        filterBy: operationsPartnerTransactions,
        ...params,
      })) as PartnerOperationsResponse;

      const transactions = mapOperationsToTransactions(
        filteredOperations.data?.filter(
          (operation) => !excludedOperations.includes(operation.operationType)
        ) || [],
        partner
      ) as [];

      setPagination(filteredOperations.pagination);

      const filteredTransactions = transactions?.filter(
        (transaction: any) => transaction?.partner === partner._id
      );

      setPartnerTransactions(filteredTransactions);

      const documentIds = getDocumentIdsFromTransactions(filteredTransactions);

      if (documentIds?.length > 0) {
        const partnerDocuments = await store.dispatch(
          getDocumentsByIds({ documentIds })
        );
        setDocuments(partnerDocuments);
      }
    } catch {
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [items, page, partner, society._id, sortBy, sortOrder]);

  const fetchTransactionsSharesTitles = useCallback(async () => {
    if (!partnerTransactions?.length || !partner?._id) return;
    setIsLoadingSharesTitles(true);

    const operationIds =
      partnerTransactions?.map(
        (transaction: Transaction) => transaction?.operation || ''
      ) || [];

    const uniqueOperationIds = Array.from(new Set(operationIds));

    const MAX_SHARES_TITLES_ALLOWED = items || 20;

    try {
      const transactionShareTitles = await store.dispatch(
        getSharesTitlesByPartnerOperations(
          partner._id,
          uniqueOperationIds.slice(0, MAX_SHARES_TITLES_ALLOWED)
        )
      );
      setPartnerShareTitles(transactionShareTitles);
    } catch (error) {
      console.error('Error fetching share titles:', error); // eslint-disable-line
      setPartnerShareTitles([]);
    } finally {
      setIsLoadingSharesTitles(false);
    }
  }, [items, partnerTransactions, partner?._id]);

  useEffect(() => {
    fetchPartnerTransactions();
  }, [fetchPartnerTransactions]);

  useEffect(() => {
    fetchTransactionsSharesTitles();
  }, [fetchTransactionsSharesTitles]);

  return {
    data: { partnerTransactions, partnerShareTitles, documents },
    metadata: { partnerShareTitles },
    isLoading,
    isLoadingSharesTitles,
    pagination,
  };
};
